import type { Session } from '@supabase/supabase-js';
import { logger } from '@/shared/utils/logger';

/**
 * Authenticator Assurance Level letto dal JWT.
 * 'aal1' = login conventionale; 'aal2' = secondo fattore verificato.
 */
export type Aal = 'aal1' | 'aal2';

/** Lista di ruoli che richiedono MFA quando il DB non è ancora disponibile. */
export const MFA_REQUIRED_ROLES_FALLBACK: readonly string[] = ['super-admin', 'amministrazione'];

/**
 * Decodifica il claim `aal` dal payload del JWT della sessione, SINCRONICAMENTE,
 * senza alcuna chiamata di rete. Il claim è garantito da Supabase Auth nel JWT
 * (vedi docs: aal1 per login base, aal2 dopo verifica del secondo fattore).
 *
 * @returns 'aal1' | 'aal2' se decodificabile, altrimenti null (token assente/illeggibile).
 */
export function decodeAalFromSession(session: Session | null): Aal | null {
  const token = session?.access_token;
  if (!token) return null;

  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;

    // base64url → base64, poi decode. atob è disponibile nel browser.
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(json) as { aal?: string };

    if (payload.aal === 'aal2') return 'aal2';
    if (payload.aal === 'aal1') return 'aal1';
    return null;
  } catch (err) {
    logger.error('MFA: impossibile decodificare aal dal JWT', err);
    return null;
  }
}

export interface MfaGate {
  /** Il ruolo dell'utente richiede MFA. */
  mfaRequired: boolean;
  /**
   * Il requisito MFA è soddisfatto: o non serve MFA, oppure la sessione è già aal2.
   * In caso di dubbio (aal non decodificabile + ruolo che richiede MFA) → FALSE (fail-closed).
   */
  mfaSatisfied: boolean;
}

/**
 * Calcola lo stato del gate MFA in modo SINCRONO a partire dalla sessione e dai ruoli.
 * Fail-closed: se il ruolo richiede MFA ma l'aal non è decodificabile, mfaSatisfied = false.
 *
 * @param session   sessione Supabase corrente (può essere null)
 * @param roleCodes codici ruolo dell'utente
 * @param mfaRequiredRoles lista (da DB o fallback) dei ruoli che richiedono MFA
 */
export function computeMfaGate(
  session: Session | null,
  roleCodes: string[],
  mfaRequiredRoles: readonly string[] = MFA_REQUIRED_ROLES_FALLBACK
): MfaGate {
  const mfaRequired = roleCodes.some(rc => mfaRequiredRoles.includes(rc));

  if (!mfaRequired) {
    return { mfaRequired: false, mfaSatisfied: true };
  }

  const aal = decodeAalFromSession(session);
  // Soddisfatto SOLO se possiamo confermare aal2. aal1 o null (dubbio) → fail-closed.
  const mfaSatisfied = aal === 'aal2';

  return { mfaRequired: true, mfaSatisfied };
}
